import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Paper,
  alpha,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  Receipt,
  Inventory,
  AttachMoney,
  Refresh,
  Visibility,
  ContactMail,
  Assignment,
  LocalShipping,
  ReceiptLong,
  PriceCheck,
  PersonAdd,
  Storefront,
  AddShoppingCart,
  RequestQuote,
  PointOfSale,
  AssignmentReturn,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DashboardMetrics {
  sales: { totalRevenue: number; totalOrders: number; pendingOrders: number; revenueGrowth: number };
  purchase: { totalPurchase: number; totalBills: number; pendingBills: number; overduePayments: number };
  leads: { totalLeads: number; newLeads: number; convertedLeads: number; conversionRate: number };
  inventory: { totalProducts: number; totalStockValue: number; lowStockItems: number; outOfStockItems: number };
}

interface TrendPoint { month: string; revenue: number; purchase: number; leads: number }

interface RecentOrder {
  id: string; order_number: string; customer_name: string;
  total_amount: number; status: string; created_at: string;
}

interface RecentLead {
  id: string; name: string; company: string;
  phone: string; status: string; source: string; created_at: string;
}

// ─── Formatters ───────────────────────────────────────────────────────────────

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const formatShort = (v: number) => {
  if (v >= 10_00_000) return `₹${(v / 10_00_000).toFixed(1)}L`;
  if (v >= 1_000) return `₹${(v / 1_000).toFixed(0)}K`;
  return `₹${v}`;
};

// ─── Metric Card ──────────────────────────────────────────────────────────────

interface MetricCardProps {
  title: string; value: string | number; sub: string;
  icon: React.ReactNode; color: string; growth?: number;
  onClick?: () => void; sparkData?: number[];
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, sub, icon, color, growth, onClick, sparkData }) => {
  const theme = useTheme();
  const upTrend = growth !== undefined && growth >= 0;
  const sparkPoints = sparkData || [0];

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%', cursor: onClick ? 'pointer' : 'default',
        border: `1px solid ${alpha(color, 0.15)}`,
        boxShadow: `0 2px 16px ${alpha(color, 0.08)}`,
        transition: 'all 0.2s ease',
        '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${alpha(color, 0.18)}` } : {},
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Decorative gradient blob */}
      <Box sx={{
        position: 'absolute', top: -20, right: -20, width: 100, height: 100,
        borderRadius: '50%', background: alpha(color, 0.08),
      }} />
      <CardContent sx={{ pb: '16px !important' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" letterSpacing={0.8}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700} mt={0.5} color="text.primary">
              {value}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.12), color, width: 44, height: 44 }}>
            {icon}
          </Avatar>
        </Box>

        {/* Mini Sparkline */}
        {sparkData && sparkData.length > 1 && (
          <Box sx={{ height: 40, mx: -1, mb: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData.map((v, i) => ({ v, i }))}>
                <defs>
                  <linearGradient id={`spark-${title}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2}
                  fill={`url(#spark-${title})`} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">{sub}</Typography>
          {growth !== undefined && (
            <Box display="flex" alignItems="center" gap={0.3}>
              {upTrend ? <ArrowUpward sx={{ fontSize: 14, color: 'success.main' }} />
                : <ArrowDownward sx={{ fontSize: 14, color: 'error.main' }} />}
              <Typography variant="caption" fontWeight={600}
                color={upTrend ? 'success.main' : 'error.main'}>
                {Math.abs(growth).toFixed(1)}%
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Status helpers ───────────────────────────────────────────────────────────

const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const map: Record<string, any> = {
    draft: 'default', pending: 'warning', new: 'info', contacted: 'primary',
    qualified: 'success', converted: 'success', lost: 'error',
    confirmed: 'info', processing: 'primary', shipped: 'secondary',
    delivered: 'success', cancelled: 'error',
  };
  return map[status?.toLowerCase()] || 'default';
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const MainDashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [trends, setTrends] = useState<TrendPoint[]>([]);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [recentLeads, setRecentLeads] = useState<RecentLead[]>([]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [metricsRes, trendsRes, ordersRes, leadsRes] = await Promise.all([
        api.get('/admin/dashboard/metrics'),
        api.get('/admin/dashboard/trends', { params: { months: 6 } }),
        api.get('/admin/sales-orders', { params: { page: 1, per_page: 5 } }),
        api.get('/admin/leads', { params: { page: 1, per_page: 5 } }),
      ]);

      setMetrics(metricsRes.data);
      setTrends(trendsRes.data.trends || []);

      const rawOrders = ordersRes.data.sales_orders || ordersRes.data.items || [];
      setRecentOrders(rawOrders.map((o: any) => ({
        id: o.id,
        order_number: o.order_number,
        customer_name: o.customer?.name || o.customer?.billing_name || o.customer_name || 'Unknown',
        total_amount: o.total_amount,
        status: typeof o.status === 'object' ? o.status.value : o.status,
        created_at: o.created_at,
      })));
      setRecentLeads(leadsRes.data.items || []);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ─── Quick action groups ──────────────────────────────────────────────────

  const actionGroups = [
    {
      label: 'Sales', gradient: 'linear-gradient(135deg,#1565c0,#1976d2)',
      lightBg: '#e3f2fd', borderColor: '#1976d2',
      actions: [
        { label: 'New Estimate', icon: <RequestQuote />, path: '/admin/documents/estimates/create' },
        { label: 'New Sales Order', icon: <AddShoppingCart />, path: '/admin/documents/sales-orders/create' },
        { label: 'New Invoice', icon: <ReceiptLong />, path: '/admin/documents/invoices/create' },
        { label: 'Sales Return', icon: <AssignmentReturn />, path: '/admin/documents/sales-returns/create' },
        { label: 'Add Customer', icon: <PersonAdd />, path: '/admin/parties/customers/create' },
        { label: 'Add Lead', icon: <ContactMail />, path: '/admin/leads/create' },
      ],
    },
    {
      label: 'Purchase', gradient: 'linear-gradient(135deg,#6a1b9a,#8e24aa)',
      lightBg: '#f3e5f5', borderColor: '#8e24aa',
      actions: [
        { label: 'New Purchase Order', icon: <ShoppingBag />, path: '/admin/purchase/orders/create' },
        { label: 'New Purchase Bill', icon: <Receipt />, path: '/admin/purchase/bills/create' },
        { label: 'Add Supplier', icon: <Storefront />, path: '/admin/parties/suppliers/create' },
        { label: 'Add Product', icon: <Inventory />, path: '/admin/products/create' },
        { label: 'Delivery Challan', icon: <LocalShipping />, path: '/admin/documents/delivery-challans/create' },
        { label: 'Stock Adjustment', icon: <Assignment />, path: '/admin/inventory/adjustments' },
      ],
    },
    {
      label: 'Accounts', gradient: 'linear-gradient(135deg,#1b5e20,#2e7d32)',
      lightBg: '#e8f5e9', borderColor: '#2e7d32',
      actions: [
        { label: 'Record Payment', icon: <AttachMoney />, path: '/admin/payments/create' },
        { label: 'Party Ledgers', icon: <PointOfSale />, path: '/admin/parties/ledgers' },
        { label: 'Purchase Returns', icon: <AssignmentReturn />, path: '/admin/purchase/returns' },
        { label: 'Analytics', icon: <PriceCheck />, path: '/admin/analytics/sales' },
      ],
    },
  ];

  // ─── Leads donut data ─────────────────────────────────────────────────────
  const leadsDonut = metrics ? [
    { name: 'Converted', value: metrics.leads.convertedLeads, color: '#2e7d32' },
    { name: 'New', value: metrics.leads.newLeads, color: '#1976d2' },
    { name: 'Other', value: Math.max(0, metrics.leads.totalLeads - metrics.leads.convertedLeads - metrics.leads.newLeads), color: '#ed6c02' },
  ].filter((d) => d.value > 0) : [];

  // ─── Inventory bar data ───────────────────────────────────────────────────
  const inventoryBar = metrics ? [
    { name: 'Total', value: metrics.inventory.totalProducts, fill: '#1976d2' },
    { name: 'Low Stock', value: metrics.inventory.lowStockItems, fill: '#ed6c02' },
    { name: 'Out of Stock', value: metrics.inventory.outOfStockItems, fill: '#d32f2f' },
  ] : [];

  // ─── Sparklines from trend data ───────────────────────────────────────────
  const revSpark = trends.map((t) => t.revenue);
  const purSpark = trends.map((t) => t.purchase);
  const leadSpark = trends.map((t) => t.leads);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="text.primary">
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.3}>
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
        <Tooltip title="Refresh data">
          <IconButton onClick={fetchAll} color="primary"
            sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider', boxShadow: 1 }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── KPI Cards ── */}
      <Grid container spacing={2.5} mb={3}>
        {loading ? (
          [0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={160} />
            </Grid>
          ))
        ) : metrics && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Sales Revenue" value={formatCurrency(metrics.sales.totalRevenue)}
                sub={`${metrics.sales.totalOrders} orders · ${metrics.sales.pendingOrders} pending`}
                icon={<Receipt />} color="#1976d2" growth={metrics.sales.revenueGrowth}
                sparkData={revSpark} onClick={() => navigate('/admin/sales/dashboard')} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Purchase Amount" value={formatCurrency(metrics.purchase.totalPurchase)}
                sub={`${metrics.purchase.totalBills} bills · ${metrics.purchase.pendingBills} pending`}
                icon={<ShoppingBag />} color="#8e24aa"
                sparkData={purSpark} onClick={() => navigate('/admin/purchase/dashboard')} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Total Leads" value={metrics.leads.totalLeads}
                sub={`${metrics.leads.conversionRate.toFixed(1)}% conversion rate`}
                icon={<ContactMail />} color="#ed6c02"
                sparkData={leadSpark} onClick={() => navigate('/admin/leads')} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard title="Stock Value" value={formatCurrency(metrics.inventory.totalStockValue)}
                sub={`${metrics.inventory.totalProducts} products · ${metrics.inventory.outOfStockItems} OOS`}
                icon={<Inventory />} color="#0288d1"
                onClick={() => navigate('/admin/inventory')} />
            </Grid>
          </>
        )}
      </Grid>

      {/* ── Revenue vs Purchase Chart ── */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>Revenue vs Purchase</Typography>
                  <Typography variant="caption" color="text.secondary">Last 6 months comparison</Typography>
                </Box>
              </Box>
              {loading ? <Skeleton variant="rectangular" height={220} /> : (
                <ResponsiveContainer width="100%" height={230}>
                  <AreaChart data={trends} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1976d2" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#1976d2" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradPur" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8e24aa" stopOpacity={0.18} />
                        <stop offset="95%" stopColor="#8e24aa" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={formatShort} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <ReTooltip
                      formatter={(value: any, name: string) => [formatCurrency(value), name === 'revenue' ? 'Revenue' : 'Purchase']}
                      contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}
                    />
                    <Legend formatter={(v) => v === 'revenue' ? 'Revenue' : 'Purchase'} />
                    <Area type="monotone" dataKey="revenue" stroke="#1976d2" strokeWidth={2.5}
                      fill="url(#gradRev)" dot={{ r: 4, fill: '#1976d2' }} activeDot={{ r: 6 }} />
                    <Area type="monotone" dataKey="purchase" stroke="#8e24aa" strokeWidth={2.5}
                      fill="url(#gradPur)" dot={{ r: 4, fill: '#8e24aa' }} activeDot={{ r: 6 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Leads Donut */}
        <Grid item xs={12} md={4}>
          <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={0.5}>Lead Status</Typography>
              <Typography variant="caption" color="text.secondary">Breakdown by category</Typography>
              {loading ? <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto', mt: 2 }} /> : (
                leadsDonut.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={leadsDonut} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                        paddingAngle={4} dataKey="value">
                        {leadsDonut.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <ReTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height={220}>
                    <Typography color="text.secondary" variant="body2">No lead data yet</Typography>
                  </Box>
                )
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Leads Monthly Bar + Inventory ── */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} md={7}>
          <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={0.5}>Monthly Leads</Typography>
              <Typography variant="caption" color="text.secondary">New leads per month</Typography>
              {loading ? <Skeleton variant="rectangular" height={200} sx={{ mt: 1 }} /> : (
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={trends} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <ReTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }} />
                    <Bar dataKey="leads" name="Leads" fill="#ed6c02" radius={[5, 5, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={0.5}>Inventory Health</Typography>
              <Typography variant="caption" color="text.secondary">Products by stock status</Typography>
              {loading ? <Skeleton variant="rectangular" height={200} sx={{ mt: 1 }} /> : (
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={inventoryBar} layout="vertical" margin={{ top: 10, right: 10, left: 20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                    <ReTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }} />
                    <Bar dataKey="value" name="Count" radius={[0, 5, 5, 0]} maxBarSize={32}>
                      {inventoryBar.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Quick Actions ── */}
      <Grid container spacing={2.5} mb={3}>
        {actionGroups.map((group) => (
          <Grid item xs={12} md={4} key={group.label}>
            <Paper elevation={0} sx={{ border: `1px solid ${alpha(group.borderColor, 0.2)}`, borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ background: group.gradient, px: 2.5, py: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} color="white" letterSpacing={0.5}>
                  {group.label}
                </Typography>
              </Box>
              <Box sx={{ bgcolor: group.lightBg, p: 1.5 }}>
                <Grid container spacing={1}>
                  {group.actions.map((action) => (
                    <Grid item xs={6} key={action.label}>
                      <Button fullWidth variant="contained" size="small" startIcon={action.icon}
                        onClick={() => navigate(action.path)}
                        sx={{
                          justifyContent: 'flex-start', bgcolor: 'white', color: group.borderColor,
                          border: `1px solid ${alpha(group.borderColor, 0.15)}`, boxShadow: 'none',
                          textTransform: 'none', fontWeight: 500, fontSize: '0.78rem',
                          py: 0.9, px: 1.2, borderRadius: 2,
                          '&:hover': { bgcolor: group.borderColor, color: 'white', boxShadow: `0 4px 12px ${alpha(group.borderColor, 0.4)}` },
                          '& .MuiButton-startIcon': { mr: 0.5 },
                        }}>
                        {action.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── Recent Tables ── */}
      <Grid container spacing={2.5}>
        {/* Recent Orders */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={700}>Recent Orders</Typography>
                <Button size="small" endIcon={<Visibility />} onClick={() => navigate('/admin/documents/sales-orders')}>
                  View All
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                      <TableCell>Order #</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      [0, 1, 2, 3, 4].map((i) => (
                        <TableRow key={i}>
                          <TableCell colSpan={4}><Skeleton /></TableCell>
                        </TableRow>
                      ))
                    ) : recentOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">No recent orders</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentOrders.map((order) => (
                        <TableRow key={order.id} hover sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/admin/documents/sales-orders/${order.id}`)}>
                          <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>{order.order_number}</TableCell>
                          <TableCell>{order.customer_name}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(order.total_amount)}</TableCell>
                          <TableCell>
                            <Chip label={order.status} size="small" color={getStatusColor(order.status)}
                              sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Leads */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={700}>Recent Leads</Typography>
                <Button size="small" endIcon={<Visibility />} onClick={() => navigate('/admin/leads')}>
                  View All
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                      <TableCell>Name</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      [0, 1, 2, 3, 4].map((i) => (
                        <TableRow key={i}>
                          <TableCell colSpan={4}><Skeleton /></TableCell>
                        </TableRow>
                      ))
                    ) : recentLeads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">No recent leads</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      recentLeads.map((lead) => (
                        <TableRow key={lead.id} hover sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/admin/leads/${lead.id}`)}>
                          <TableCell sx={{ fontWeight: 600 }}>{lead.name}</TableCell>
                          <TableCell>{lead.company || '—'}</TableCell>
                          <TableCell>
                            <Chip label={lead.source} size="small" variant="outlined"
                              sx={{ textTransform: 'capitalize', fontWeight: 500 }} />
                          </TableCell>
                          <TableCell>
                            <Chip label={lead.status} size="small" color={getStatusColor(lead.status)}
                              sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MainDashboard;
